/**
 * payrollColumns.ts — Shared payroll column definitions.
 *
 * Architecture: Option B — Individual + Groups
 *
 *   - Each column is an individual exported function: `salaryColumn(params)`
 *   - Groups compose individuals for convenience: `getIrelandTaxColumns(params)`
 *   - Pages can cherry-pick individuals OR spread full groups
 *
 * Design rules:
 *
 *   1. Every column function takes `{ messages, payrollCountry }` and derives
 *      currencySymbol / isIreland internally.
 *
 *   2. Ireland payrolls have `payeeUmbrellaValue = {}` — Ireland-specific
 *      columns read from `irelandValue`. UK columns read from `payeeUmbrellaValue`.
 *
 *   3. `_otherDeduction` can be negative — always apply `Math.abs`.
 *
 *   4. `employeeNI` rendered WITHOUT `Math.max(0)` — negative = valid
 *      director NI refund.
 *
 *   5. UK AVC reads from `payeeUmbrellaValue`; Ireland AVC reads from
 *      `irelandValue.privatePensions` and is kept out of the private pension columns.
 *
 *   6. Payslip ID from `selfBillRef.id` (replace 'SFB' → 'PAY'), NOT `record.id`.
 *
 *   7. Pension shows amount OR negated refund as fallback. Under salary
 *      sacrifice EE/ER/AVC EE show the backend's reported split instead
 *      (see utils/reported-pension).
 *
 *   8. pensionDeductionMethods.find() has fallback to prevent rendering "undefined".
 */

import {
  defaultPaymentFrequency,
  defaultTaxBasisOptions,
  PAYMENT_MODEL_ENUM,
  pensionDeductionMethods,
} from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { IRELAND } from '@nxsys/store/constants/keys';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import {
  getShownEmployeePension,
  getShownEmployersPension,
  getShownPensionValue,
} from '@nxsys/utils/reported-pension';
import { ColumnsType } from 'antd/es/table';
import { marginFigures } from './margin-figures';
import { find } from 'lodash';
import moment from 'moment';

// ---------------------------------------------------------------------------
// Params type
// ---------------------------------------------------------------------------

export interface PayrollColumnParams {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}

type Col = ColumnsType<DraftPayroll>[number];

function cx(p: PayrollColumnParams) {
  return showCurrencyByCountry(p.payrollCountry);
}

// ===========================================================================
// INDIVIDUAL COLUMN FUNCTIONS
// ===========================================================================

// ---------------------------------------------------------------------------
// Ireland Tax (13)
// ---------------------------------------------------------------------------

export const notionalPayColumn = (p: PayrollColumnParams): Col => ({
  key: 'notionalPay',
  width: 180,
  title: p.messages['common.notionalPay'],
  align: 'right',
  render: (record) =>
    formatMoney(record?.irelandValue?.notionalPay || 0, cx(p)),
});

export const taxableGrossColumn = (p: PayrollColumnParams): Col => ({
  key: 'grossForTax',
  width: 200,
  title: p.messages['common.taxableGross'] || 'Taxable Gross',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.taxGross || 0, cx(p)),
});

export const taxDeductedIEColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxDeducted',
  width: 200,
  title: p.messages['common.taxDeducted'],
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.tax || 0, cx(p)),
});

export const uscGrossColumn = (p: PayrollColumnParams): Col => ({
  key: 'uscGross',
  width: 200,
  title: p.messages['common.uscAbleGross'] || 'USCable Gross',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.uscGross || 0, cx(p)),
});

export const uscColumn = (p: PayrollColumnParams): Col => ({
  key: 'usc',
  width: 150,
  title: p.messages['common.usc'] || 'USC',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.usc || 0, cx(p)),
});

export const eePrsiGrossColumn = (p: PayrollColumnParams): Col => ({
  key: 'eePrsiGross',
  title:
    p.messages['common.employeePrsiAbleGross'] || 'Employee PRSIable Gross',
  align: 'right',
  dataIndex: 'irelandValue',
  width: 220,
  render: (value) => formatMoney(value?.eePrsiGross || 0, cx(p)),
});

export const eePRSIColumn = (p: PayrollColumnParams): Col => ({
  key: 'eePRSI',
  width: 200,
  title: p.messages['common.employeePrsi'] || 'Employee PRSI',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.eePRSI || 0, cx(p)),
});

export const erPrsiGrossColumn = (p: PayrollColumnParams): Col => ({
  key: 'erPrsiGross',
  title:
    p.messages['common.employerPrsiAbleGross'] || 'Employer PRSIable Gross',
  align: 'right',
  dataIndex: 'irelandValue',
  width: 220,
  render: (value) => formatMoney(value?.erPrsiGross || 0, cx(p)),
});

export const erPRSIColumn = (p: PayrollColumnParams): Col => ({
  key: 'erPRSI',
  width: 200,
  title: p.messages['common.employerPrsi'] || 'Employer PRSI',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.erPRSI || 0, cx(p)),
});

export const pensionGrossColumn = (p: PayrollColumnParams): Col => ({
  key: 'pensionGross',
  width: 200,
  title: p.messages['common.pensionableGross'] || 'Pensionable Gross',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.pensionGross || 0, cx(p)),
});

export const eePensionIEColumn = (p: PayrollColumnParams): Col => ({
  key: 'eePension',
  width: 200,
  title: p.messages['common.employeePension'],
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.eePension || 0, cx(p)),
});

export const erPensionIEColumn = (p: PayrollColumnParams): Col => ({
  key: 'erPension',
  width: 200,
  title: p.messages['common.employerPension'],
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.erPension || 0, cx(p)),
});

export const lptColumn = (p: PayrollColumnParams): Col => ({
  key: 'lptDeduction',
  width: 150,
  title: p.messages['common.lpt'] || 'LPT',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.lptDeducted || 0, cx(p)),
});

export const privatePensionGrossColumn = (p: PayrollColumnParams): Col => ({
  key: 'privatePensionGross',
  width: 250,
  title: p.messages['common.privatePensionGross'] || 'Private Pensionable Gross',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) => formatMoney(value?.privatePensionGross || 0, cx(p)),
});

export const eePrivatePensionColumn = (p: PayrollColumnParams): Col => ({
  key: 'eePrivatePension',
  width: 250,
  title: p.messages['common.employeePrivatePension'] || 'Employee Private Pension',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) =>
    formatMoney(
      value?.privatePensions?.reduce(
        (acc: number, cur: any) => acc + (cur.employeePension || 0),
        0,
      ) || 0,
      cx(p),
    ),
});

export const erPrivatePensionColumn = (p: PayrollColumnParams): Col => ({
  key: 'erPrivatePension',
  width: 250,
  title: p.messages['common.employerPrivatePension'] || 'Employer Private Pension',
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) =>
    formatMoney(
      value?.privatePensions?.reduce(
        (acc: number, cur: any) => acc + (cur.employerPension || 0),
        0,
      ) || 0,
      cx(p),
    ),
});

// ---------------------------------------------------------------------------
// UK Tax (6)
// ---------------------------------------------------------------------------

export const taxablePayColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxablePay',
  width: 200,
  title: p.messages['common.taxablePay'],
  align: 'right',
  render: (record) =>
    formatMoney(
      record?.displayedTaxablePay || record?.payeeUmbrellaValue?.taxablePay,
      cx(p),
    ),
});

export const grossForTaxColumn = (p: PayrollColumnParams): Col => ({
  key: 'grossForTax',
  width: 200,
  title: p.messages['common.grossForTax'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.grossForTax, cx(p)),
});

export const grossForNIColumn = (p: PayrollColumnParams): Col => ({
  key: 'grossForNI',
  width: 200,
  title: p.messages['common.grossForNI'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.grossForNI, cx(p)),
});

/** No Math.max(0) — negative values are valid director NI refunds. */
export const taxDeductedUKColumn = (p: PayrollColumnParams): Col => ({
  key: 'tax',
  width: 200,
  title: p.messages['common.taxDeducted'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.tax, cx(p)),
});

/** No Math.max(0) — negative values are valid director NI refunds. */
export const employeeNIColumn = (p: PayrollColumnParams): Col => ({
  key: 'employeeNI',
  width: 200,
  title: p.messages['common.niEmpe'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.employeeNI, cx(p)),
});

export const employersNIColumn = (p: PayrollColumnParams): Col => ({
  key: 'employersNI',
  width: 200,
  title: p.messages['common.niEmpr'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.employersNI, cx(p)),
});

// ---------------------------------------------------------------------------
// Country-Aware (4) — data source differs by country
// ---------------------------------------------------------------------------

/** Salary: IE reads irelandValue.salary, UK reads payeeUmbrellaValue.salary */
export const salaryColumn = (p: PayrollColumnParams): Col => {
  const isIreland = p.payrollCountry === IRELAND;
  return {
    key: 'salary',
    width: 200,
    title: p.messages['common.salary'],
    align: 'right',
    render: (record) =>
      isIreland
        ? formatMoney(record?.irelandValue?.salary, cx(p))
        : formatMoney(record?.payeeUmbrellaValue?.salary, cx(p)),
  };
};

/** Units: IE sums timeSheets[].rates[].units, UK reads payeeUmbrellaValue.totalHours */
export const unitsColumn = (p: PayrollColumnParams): Col => {
  const isIreland = p.payrollCountry === IRELAND;
  return {
    key: 'hours',
    width: 150,
    title: isIreland ? p.messages['common.units'] : p.messages['common.hours'],
    align: 'right',
    render: (record) =>
      record?.totalHours ||
      record?.payeeUmbrellaValue?.totalHours ||
      record?._totalHours ||
      0,
  };
};

/** Tax deducted (country-aware fallback): payeeUmbrellaValue.tax || irelandValue.tax */
export const taxDeductedColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxDeductedCountry',
  title: p.messages['common.taxDeducted'],
  align: 'right',
  width: 180,
  render: (record) =>
    formatMoney(
      record?.payeeUmbrellaValue?.tax || record?.irelandValue?.tax || 0,
      cx(p),
    ),
});

/** CIS/RCT Status — title changes per country */
export const cisStatusColumn = (p: PayrollColumnParams): Col => {
  const isIreland = p.payrollCountry === IRELAND;
  return {
    key: 'cisStatus',
    title: isIreland
      ? p.messages['common.rctStatus'] || 'RCT Status'
      : p.messages['table.cisStatus'],
    align: 'center',
    dataIndex: 'cisStatus',
    width: 180,
    sorter: true,
    sortDirections: ['descend', 'ascend'],
  };
};

/** CIS/RCT Deduction — title and width change per country */
export const cisDeductionColumn = (p: PayrollColumnParams): Col => {
  const isIreland = p.payrollCountry === IRELAND;
  return {
    key: 'cisDeduction',
    title: isIreland
      ? (p.messages['common.rctDeduction'] || 'RCT Deduction')
      : p.messages['common.cisDeduction'],
    align: 'right',
    dataIndex: 'cisDeduction',
    width: isIreland ? 200 : 250,
    render: (value) => formatMoney(value || 0, cx(p)),
    sorter: true,
    sortDirections: ['descend', 'ascend'],
  };
};

// ---------------------------------------------------------------------------
// Pension (6)
// ---------------------------------------------------------------------------

export const pensionEmpeColumn = (p: PayrollColumnParams): Col => ({
  key: 'pensionEmpe',
  title: p.messages['common.pensionEmpe'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (_, record) => formatMoney(getShownEmployeePension(record), cx(p)),
});

export const eePensionMethodColumn = (p: PayrollColumnParams): Col => ({
  key: 'eePensionDeductionMethod',
  title: p.messages['common.empeDeductionMethod'],
  dataIndex: 'payeeUmbrellaValue',
  width: 250,
  render: (value) => {
    const method = value?.eePensionDeductionMethod;
    return method
      ? pensionDeductionMethods.find((item) => item.value === method)?.label ||
          p.messages['common.percentage']
      : p.messages['common.percentage'];
  },
});

export const avcEmployeeColumn = (p: PayrollColumnParams): Col => ({
  key: 'avcEmployee',
  width: 200,
  title: p.messages['common.avcEmployee'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  // Under salary sacrifice the employee AVC is reported inside the employer
  // pension, so it must not be shown again here.
  render: (_, record) =>
    formatMoney(getShownPensionValue(record, 'avcEEPension'), cx(p)),
});

/** Ireland AVC lives on irelandValue.privatePensions — payeeUmbrellaValue is empty for IE. */
export const avcEmployeeIEColumn = (p: PayrollColumnParams): Col => ({
  key: 'avcEmployee',
  width: 200,
  title: p.messages['common.avcEmployee'],
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) =>
    formatMoney(
      value?.privatePensions?.reduce(
        (acc: number, cur: any) => acc + (cur.employeeAvc || 0),
        0,
      ) || 0,
      cx(p),
    ),
});

export const pensionEmprColumn = (p: PayrollColumnParams): Col => ({
  key: 'pensionEmpr',
  title: p.messages['common.pensionEmpr'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (_, record) => formatMoney(getShownEmployersPension(record), cx(p)),
});

export const erPensionMethodColumn = (p: PayrollColumnParams): Col => ({
  key: 'erPensionDeductionMethod',
  title: p.messages['common.emprDeductionMethod'],
  dataIndex: 'payeeUmbrellaValue',
  width: 250,
  render: (value) => {
    const method = value?.erPensionDeductionMethod;
    return method
      ? pensionDeductionMethods.find((item) => item.value === method)?.label ||
          p.messages['common.percentage']
      : p.messages['common.percentage'];
  },
});

export const avcEmployerColumn = (p: PayrollColumnParams): Col => ({
  key: 'avcEmployer',
  width: 200,
  title: p.messages['common.avcEmployer'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.avcERPension, cx(p)),
});

export const avcEmployerIEColumn = (p: PayrollColumnParams): Col => ({
  key: 'avcEmployer',
  width: 200,
  title: p.messages['common.avcEmployer'],
  align: 'right',
  dataIndex: 'irelandValue',
  render: (value) =>
    formatMoney(
      value?.privatePensions?.reduce(
        (acc: number, cur: any) => acc + (cur.employerAvc || 0),
        0,
      ) || 0,
      cx(p),
    ),
});

// ---------------------------------------------------------------------------
// UK Statutory (5)
// ---------------------------------------------------------------------------

export const sspColumn = (p: PayrollColumnParams): Col => ({
  key: 'ssp',
  width: 200,
  title: p.messages['common.sickPaySSP'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.ssp || 0, cx(p)),
});

export const smpColumn = (p: PayrollColumnParams): Col => ({
  key: 'smp',
  title: p.messages['common.maternityPaySMP'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (value) => formatMoney(value?.smp, cx(p)),
});

export const sapColumn = (p: PayrollColumnParams): Col => ({
  key: 'sap',
  title: p.messages['common.adoptionPaySAP'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (value) => formatMoney(value?.sap, cx(p)),
});

export const sppColumn = (p: PayrollColumnParams): Col => ({
  key: 'spp',
  title: p.messages['common.paternityPaySPP'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (value) => formatMoney(value?.spp || value?.sppa, cx(p)),
});

export const additionalSPPColumn = (p: PayrollColumnParams): Col => ({
  key: 'additionalSPP',
  width: 200,
  title: p.messages['common.additionalSPP'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.additionalSPP, cx(p)),
});

export const spbpColumn = (p: PayrollColumnParams): Col => ({
  key: 'spbp',
  title: p.messages['common.parentalBereavementPaySPBP'] || 'Parental Bereavement Pay (SPBP)',
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 350,
  render: (value) => formatMoney(value?.spbp, cx(p)),
});

// ---------------------------------------------------------------------------
// UK Deductions (4)
// ---------------------------------------------------------------------------

export const studentLoanColumn = (p: PayrollColumnParams): Col => ({
  key: 'studentLoan',
  width: 200,
  title: p.messages['common.studentLoan'],
  align: 'right',
  dataIndex: 'studentLoan',
  render: (value) => formatMoney(value, cx(p)),
});

export const postgraduateLoanColumn = (p: PayrollColumnParams): Col => ({
  key: 'postgraduateLoan',
  title: p.messages['common.postgraduateLoan'],
  align: 'right',
  dataIndex: 'postgraduateLoan',
  width: 200,
  render: (value) => formatMoney(value, cx(p)),
});

export const attachmentColumn = (p: PayrollColumnParams): Col => ({
  key: 'attachment',
  width: 200,
  title: p.messages['common.attachment'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) =>
    formatMoney(value?.attachmentOrderDeducted || 0, cx(p)),
});

export const aoMarginColumn = (p: PayrollColumnParams): Col => ({
  key: 'aoMargin',
  width: 200,
  title: p.messages['common.aoMargin'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) =>
    formatMoney(value?.attachmentOrderMarginDeducted || 0, cx(p)),
});

export const apprenticeLevyColumn = (p: PayrollColumnParams): Col => ({
  key: 'appLevy',
  width: 200,
  title: p.messages['common.apprenticeLevy'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.appLevy || 0, cx(p)),
});

// ---------------------------------------------------------------------------
// UK YTD (2)
// ---------------------------------------------------------------------------

export const grossForTaxYTDColumn = (p: PayrollColumnParams): Col => ({
  key: 'grossForTaxYTD',
  title: p.messages['common.grossForTaxYTD'],
  align: 'right',
  width: 200,
  render: (record) =>
    formatMoney(
      (record?.payeeUmbrellaValue?.grossForTaxTD || 0) +
        (record?.payeeUmbrellaValue?.p45GrossToDate || 0) +
        (record?.payeeUmbrellaValue?.p11?.grossForTax || 0),
      cx(p),
    ),
});

export const taxYTDColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxYTD',
  width: 200,
  title: p.messages['common.taxYTD'],
  align: 'right',
  render: (record) =>
    formatMoney(
      (record?.payeeUmbrellaValue?.taxTD || 0) +
        (record?.payeeUmbrellaValue?.p45TaxToDate || 0) +
        (record?.payeeUmbrellaValue?.p11?.tax || 0),
      cx(p),
    ),
});

// ---------------------------------------------------------------------------
// Holiday (3)
// ---------------------------------------------------------------------------

export const advancedHolidayPayColumn = (p: PayrollColumnParams): Col => ({
  key: 'holidayPay',
  title: p.messages['common.advancedHolidayPay'],
  align: 'right',
  dataIndex: 'rolledUpHolidayPay',
  width: 200,
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const holidayPayRetainedColumn = (p: PayrollColumnParams): Col => ({
  key: 'holidayPayRetained',
  title: p.messages['common.holidayPayRetained'],
  align: 'right',
  dataIndex: 'retainedHolidayPay',
  width: 200,
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const hpEmployerCostsColumn = (p: PayrollColumnParams): Col => ({
  key: 'hpEmployerCosts',
  title: p.messages['common.hpEmployerCosts'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (value) => formatMoney(value?.retainedHolidayPayErCosts || 0, cx(p)),
});

export const hpEmployerCostsPaidColumn = (p: PayrollColumnParams): Col => ({
  key: 'hpEmployerCostsPaid',
  title: p.messages['common.hpEmprCostsPaid'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 200,
  render: (value) => formatMoney(value?.hpRetainedPaidOutErCosts || 0, cx(p)),
});

export const holidayPaidColumn = (p: PayrollColumnParams): Col => ({
  key: 'holidayPaid',
  width: 200,
  title: p.messages['common.holidayPaid'],
  align: 'right',
  render: (record) =>
    formatMoney(
      (record?.holidayPaid || 0) - (record?.rolledUpHolidayPay || 0),
      cx(p),
    ),
});

// ---------------------------------------------------------------------------
// Common (6)
// ---------------------------------------------------------------------------

export const otherPaymentColumn = (p: PayrollColumnParams): Col => ({
  key: 'otherPayment',
  title: p.messages['common.otherPayment'],
  align: 'right',
  dataIndex: '_otherPayment',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 200,
});

/** _otherDeduction can be negative — always display absolute value. */
export const otherDeductionColumn = (p: PayrollColumnParams): Col => ({
  key: 'otherDeduction',
  title: p.messages['common.otherDeduction'],
  align: 'right',
  dataIndex: '_otherDeduction',
  render: (value) => formatMoney(Math.abs(value) || 0, cx(p)),
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 200,
});

export const timesheetValueColumn = (p: PayrollColumnParams): Col => ({
  key: 'timesheetValue',
  title: p.isBureau
    ? (p.messages['common.grossPay'] || 'Gross Pay')
    : p.messages['common.timesheetValue'],
  align: 'right',
  dataIndex: 'timesheetValue',
  width: 180,
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const payFrequencyColumn = (p: PayrollColumnParams): Col => ({
  key: 'payFrequency',
  title: p.messages['common.payFrequency'],
  dataIndex: 'payFrequency',
  width: 200,
  render: (value) =>
    value
      ? find(defaultPaymentFrequency, (item) => item?.value === value)?.label
      : '_',
});

/** UK only — taxCode / niCategory come from payeeUmbrellaValue, taxBasis from the payroll. */
export const taxCodeColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxCode',
  title: p.messages['common.taxCode'],
  width: 150,
  render: (record) => record?.payeeUmbrellaValue?.taxCode || '_',
});

export const taxBasisColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxBasis',
  title: p.messages['common.taxBasis'],
  width: 150,
  render: (record) =>
    record?.taxBasis
      ? find(defaultTaxBasisOptions, (item) => item.value === record.taxBasis)
          ?.label || record.taxBasis
      : '_',
});

export const niCategoryColumn = (p: PayrollColumnParams): Col => ({
  key: 'niCategory',
  title: p.messages['common.niCategory'],
  width: 150,
  render: (record) => record?.payeeUmbrellaValue?.niCategory || '_',
});

export const getUKTaxInfoColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  taxCodeColumn(p),
  taxBasisColumn(p),
  niCategoryColumn(p),
];

export const taxPeriodColumn = (p: PayrollColumnParams): Col => ({
  key: 'taxPeriod',
  title: p.messages['common.taxPeriod'],
  align: 'right',
  render: (record) =>
    `${record?.taxPeriod || ''}/${
      record?.taxYear || record?.selfBillRef?.taxYear || ''
    }`,
  sorter: true,
  width: 160,
  sortDirections: ['descend', 'ascend'],
});

export const paymentDateColumn = (p: PayrollColumnParams): Col => ({
  key: 'paymentDate',
  title: p.messages['common.paymentDate'],
  align: 'right',
  dataIndex: 'paymentDate',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  render: (value) =>
    value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  width: 200,
});

// ---------------------------------------------------------------------------
// Payment Info (2)
// ---------------------------------------------------------------------------

export const netPayColumn = (p: PayrollColumnParams): Col => ({
  key: 'netPay',
  title: p.messages['common.netPay'],
  align: 'right',
  dataIndex: 'net',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  width: 150,
  sortDirections: ['descend', 'ascend'],
});

/** Payslip ID from selfBillRef.id (NOT record.id). */
export const payslipIdColumn = (p: PayrollColumnParams): Col => ({
  key: 'payslipId',
  title: p.messages['common.payslipId'],
  dataIndex: 'selfBillRef',
  render: (value) => value?.id?.replace('SFB', 'PAY') || '_',
  width: 200,
});

/** Sales invoice numbers of the payroll's timesheets (BE fills invoiceNos). */
export const salesInvRefColumn = (p: PayrollColumnParams): Col => ({
  key: 'salesInvRef',
  title: p.messages['common.salesInvRef'],
  dataIndex: 'invoiceNos',
  // One number per line — a comma-joined list wraps inside "INV-…" at the hyphen.
  onCell: () => ({ style: { whiteSpace: 'pre-line' } }),
  // A payroll made from a timesheet carries its invoice on invoiceNo; the rest
  // list theirs in invoiceNos.
  render: (invoiceNos: string[] | undefined, record: any) => {
    const numbers = record?.timeSheetRef
      ? [record?.invoiceNo].filter(Boolean)
      : invoiceNos;
    return numbers?.length ? numbers.join('\n') : '_';
  },
  width: 200,
});

// ---------------------------------------------------------------------------
// Additional columns (12) — needed by report pages
// ---------------------------------------------------------------------------

export const troncValueColumn = (p: PayrollColumnParams): Col => ({
  key: 'troncValue',
  width: 200,
  title: p.messages['common.troncPayment'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.troncValue, cx(p)),
});

/** PAYEE rows never show a margin — keep that rule next to the columns. */
const _figures = (record: DraftPayroll) =>
  marginFigures(
    record as never,
    record?.subcontractorRef?.paymentModel === PAYMENT_MODEL_ENUM.PAYEE,
  );

export const marginColumn = (p: PayrollColumnParams): Col => ({
  key: 'margin',
  title: p.messages['common.margin'],
  align: 'right',
  render: (record) =>
    formatMoney(_figures(record).gross, cx(p)),
  sorter: true,
  width: 150,
  sortDirections: ['descend', 'ascend'],
});

export const marginAdjustmentColumn = (p: PayrollColumnParams): Col => ({
  key: 'marginAdjustment',
  title: p.messages['common.marginAdjustment'],
  align: 'right',
  render: (record) =>
    formatMoney(_figures(record).adjustment, cx(p)),
  width: 170,
});

export const netMarginColumn = (p: PayrollColumnParams): Col => ({
  key: 'netMargin',
  title: p.messages['common.netMargin'],
  align: 'right',
  render: (record) =>
    formatMoney(_figures(record).net, cx(p)),
  width: 150,
});

export const bonusColumn = (p: PayrollColumnParams): Col => ({
  key: 'bonus',
  width: 200,
  title: p.messages['common.bonus'],
  align: 'right',
  render: (record) => {
    const { paymentModel, payeeUmbrellaValue } = record;
    if (
      [PAYMENT_MODEL_ENUM.PAYEE, PAYMENT_MODEL_ENUM.HYBRID].includes(
        paymentModel,
      )
    )
      return '_';
    return formatMoney(payeeUmbrellaValue?.bonus, cx(p));
  },
});

export const expensesClaimedColumn = (p: PayrollColumnParams): Col => ({
  key: 'expensesClaimed',
  title: p.messages['common.expensesClaimed'],
  align: 'right',
  width: 200,
  // The payroll and draft payroll APIs both return expenseClaimed (singular).
  render: (record) =>
    formatMoney(record?.expenseClaimed ?? record?.expensesClaimed ?? 0, cx(p)),
});

export const expensesPaidColumn = (p: PayrollColumnParams): Col => ({
  key: 'expensesPaid',
  width: 200,
  title: p.messages['common.expensesPaid'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  render: (value) => formatMoney(value?.expensesPaid || 0, cx(p)),
});

export const totalPaymentColumn = (p: PayrollColumnParams): Col => ({
  key: 'totalPayment',
  width: 200,
  title: p.messages['common.totalPayment'],
  align: 'right',
  dataIndex: 'totalPayment',
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const totalDeductionColumn = (p: PayrollColumnParams): Col => ({
  key: 'totalDeduction',
  width: 200,
  title: p.messages['common.totalDeduction'],
  align: 'right',
  dataIndex: 'totalDeduction',
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const sncpColumn = (p: PayrollColumnParams): Col => ({
  key: 'sncp',
  title: p.messages['common.neonatalCarePaySNCP'],
  align: 'right',
  dataIndex: 'payeeUmbrellaValue',
  width: 250,
  render: (value) => formatMoney(value?.sncp, cx(p)),
});

export const fpsAmountColumn = (p: PayrollColumnParams): Col => ({
  key: 'fpsAmount',
  width: 200,
  title: p.messages['common.fpsAmount'],
  align: 'right',
  dataIndex: 'fpsAmount',
  render: (value) => formatMoney(value, cx(p)),
});

export const paidColumn = (p: PayrollColumnParams): Col => ({
  key: 'paid',
  width: 200,
  title: p.messages['common.paid'],
  align: 'right',
  dataIndex: 'paid',
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const outstandingColumn = (p: PayrollColumnParams): Col => ({
  key: 'outstanding',
  width: 200,
  title: p.messages['common.outstanding'],
  align: 'right',
  dataIndex: 'outstanding',
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const departmentColumn = (p: PayrollColumnParams): Col => ({
  key: 'department',
  width: 200,
  title: p.messages['common.department'],
  dataIndex: 'subcontractorRef',
  render: (value) => value?.department || '_',
});

// ===========================================================================
// CONVENIENCE GROUP FUNCTIONS
// ===========================================================================

export const getIrelandTaxColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  notionalPayColumn(p),
  taxableGrossColumn(p),
  taxDeductedIEColumn(p),
  uscGrossColumn(p),
  uscColumn(p),
  eePrsiGrossColumn(p),
  eePRSIColumn(p),
  erPrsiGrossColumn(p),
  erPRSIColumn(p),
  pensionGrossColumn(p),
  eePensionIEColumn(p),
  erPensionIEColumn(p),
  privatePensionGrossColumn(p),
  eePrivatePensionColumn(p),
  erPrivatePensionColumn(p),
  lptColumn(p),
];

export const getUKTaxColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  taxablePayColumn(p),
  grossForTaxColumn(p),
  grossForNIColumn(p),
  taxDeductedUKColumn(p),
  employeeNIColumn(p),
  employersNIColumn(p),
];

export const getCountryAwareColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  salaryColumn(p),
  unitsColumn(p),
  cisStatusColumn(p),
  cisDeductionColumn(p),
];

export const getPensionColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  pensionEmpeColumn(p),
  eePensionMethodColumn(p),
  avcEmployeeColumn(p),
  pensionEmprColumn(p),
  erPensionMethodColumn(p),
  avcEmployerColumn(p),
];

export const getUKStatutoryColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  sspColumn(p),
  smpColumn(p),
  sapColumn(p),
  sppColumn(p),
  additionalSPPColumn(p),
  spbpColumn(p),
];

export const getUKDeductionColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  studentLoanColumn(p),
  postgraduateLoanColumn(p),
  attachmentColumn(p),
  aoMarginColumn(p),
  apprenticeLevyColumn(p),
];

export const getUKYTDColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  grossForTaxYTDColumn(p),
  taxYTDColumn(p),
];

export const getHolidayColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  advancedHolidayPayColumn(p),
  holidayPayRetainedColumn(p),
  hpEmployerCostsColumn(p),
  hpEmployerCostsPaidColumn(p),
  holidayPaidColumn(p),
];

export const getCommonColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  otherPaymentColumn(p),
  otherDeductionColumn(p),
  timesheetValueColumn(p),
  payFrequencyColumn(p),
  taxPeriodColumn(p),
  paymentDateColumn(p),
];

export const getPaymentInfoColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  netPayColumn(p),
  payslipIdColumn(p),
];

// ===========================================================================
// MASTER SET — largest common subset across all pages
// ===========================================================================

/**
 * Returns ALL shared columns in the standard order used by Draft Payroll Report.
 * This is the largest set — 49 columns covering both Ireland and UK.
 *
 * Pages use this as a base:
 *   - Draft Report / Payment Summary / EmailPayslip: use as-is (+ per-page extras)
 *   - DraftPayrollModal / ConfirmPayroll: filter by key to pick subset
 *
 * Usage:
 *   const allCols = getAllSharedColumns(p);
 *   // Use full set
 *   const cols = [actionsCol, ...identityCols, ...allCols];
 *   // Or filter
 *   const keys = ['taxPeriod', 'payFrequency', 'pensionEmpe', ...];
 *   const cols = [actionsCol, ...allCols.filter(c => keys.includes(c.key as string))];
 */
/** First payment's description — what the Payment Summary CSV always exported. */
export const descriptionColumn = (p: PayrollColumnParams): Col => ({
  key: 'description',
  title: p.messages['common.description'],
  width: 250,
  render: (record) => record?.payments?.[0]?.description || '_',
});

/**
 * Returns ALL shared columns — the true superset (61 columns).
 * Handles Ireland/UK branching internally.
 *
 * Pages use this as a base:
 *   - Draft Report / Payment Summary / EmailPayslip: use as-is + identity + per-page extras
 *   - ConfirmPayroll: filter by key to pick subset
 *
 * NOTE: taxPeriodColumn is deliberately NOT part of this set. It belongs right
 * after the name/surname column so it stays visible without horizontal
 * scrolling, so each page adds it inline with its own identity columns.
 *
 * Usage:
 *   const allCols = getAllSharedColumns(p);
 *   const cols = [actionsCol, ...identityCols, ...allCols];
 *   // Or filter for subset:
 *   const cols = allCols.filter(c => wanted.includes(c.key as string));
 */
export const getAllSharedColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => {
  const isIreland = p.payrollCountry === IRELAND;

  return [
    // Common
    payFrequencyColumn(p),
    ...(!isIreland ? getUKTaxInfoColumns(p) : []),
    timesheetValueColumn(p),

    // Country-aware
    ...(!isIreland ? [troncValueColumn(p)] : []),
    salaryColumn(p),
    unitsColumn(p),
    ...(!isIreland && !p.isBureau
      ? [marginColumn(p), marginAdjustmentColumn(p), netMarginColumn(p)]
      : []),
    ...(!isIreland && !p.isBureau ? [bonusColumn(p)] : []),

    // Ireland tax (conditional)
    ...(isIreland ? getIrelandTaxColumns(p) : []),

    // UK tax (conditional)
    ...(!isIreland ? [taxablePayColumn(p)] : []),
    ...(!isIreland ? [grossForTaxColumn(p)] : []),
    ...(!isIreland ? [grossForNIColumn(p)] : []),

    // Tax deducted — Ireland already has it in getIrelandTaxColumns
    ...(!isIreland ? [taxDeductedColumn(p)] : []),

    // UK NI (conditional)
    ...(!isIreland ? [employeeNIColumn(p)] : []),
    ...(!isIreland ? [employersNIColumn(p)] : []),

    // Pension — Ireland has eePensionIE/erPensionIE in getIrelandTaxColumns (from irelandValue)
    // UK reads from payeeUmbrellaValue. Ireland shows private pension AVC (from irelandValue).
    ...(!isIreland
      ? [pensionEmpeColumn(p), eePensionMethodColumn(p), avcEmployeeColumn(p),
         pensionEmprColumn(p), erPensionMethodColumn(p), avcEmployerColumn(p)]
      : [avcEmployeeIEColumn(p), avcEmployerIEColumn(p)]),

    // UK statutory (conditional)
    ...(!isIreland
      ? [sspColumn(p), smpColumn(p), sncpColumn(p), sapColumn(p), sppColumn(p), additionalSPPColumn(p), spbpColumn(p)]
      : [sspColumn(p)]), // Ireland still has SSP

    // UK deductions (conditional)
    ...(!isIreland
      ? [studentLoanColumn(p), postgraduateLoanColumn(p), attachmentColumn(p), aoMarginColumn(p), apprenticeLevyColumn(p)]
      : []),

    // UK YTD (conditional)
    ...(!isIreland ? [grossForTaxYTDColumn(p), taxYTDColumn(p)] : []),

    // Holiday (always) — HP employer cost columns hidden for Bureau (UK + Ireland)
    advancedHolidayPayColumn(p),
    holidayPayRetainedColumn(p),
    ...(!p.isBureau ? [hpEmployerCostsColumn(p), hpEmployerCostsPaidColumn(p)] : []),
    holidayPaidColumn(p),

    // Expenses (UK Umbrella only — Payroll doesn't support expenses)
    ...(!isIreland && !p.isBureau ? [expensesClaimedColumn(p), expensesPaidColumn(p)] : []),

    // Common
    otherPaymentColumn(p),
    otherDeductionColumn(p),
    descriptionColumn(p),

    // Totals (UK only)
    ...(!isIreland ? [totalPaymentColumn(p), totalDeductionColumn(p)] : []),

    // Department (always)
    departmentColumn(p),

    // RCT (Ireland only) — UK PAYE/Umbrella is not CIS; CIS has its own report page
    ...(isIreland ? [cisStatusColumn(p), cisDeductionColumn(p)] : []),

    // Payment info
    netPayColumn(p),
    paymentDateColumn(p),
    payslipIdColumn(p),

    // UK Umbrella only — the old bureau portal never showed it
    ...(!isIreland && !p.isBureau ? [salesInvRefColumn(p)] : []),

    // UK only
    ...(!isIreland ? [fpsAmountColumn(p)] : []),
  ];
};

// ===========================================================================
// CONFIRMED PAYROLL EXTRAS — only exist after draft is confirmed
// ===========================================================================

/**
 * Columns that only have data on confirmed (non-draft) payrolls.
 * `paid` and `outstanding` are set during confirmation and updated during payment.
 * Append these after getAllSharedColumns for Payment Summary / confirmed report pages.
 */
export const getConfirmedPayrollColumns = (p: PayrollColumnParams): ColumnsType<DraftPayroll> => [
  paidColumn(p),
  outstandingColumn(p),
];
